import { Box, Typography } from '@mui/material';
import { SDLC_PATH } from '../../config';
import { groupPhasesByStage } from '../../content';
import { stageColourFor, stageLabelFor, tokens } from '../../theme';
import { PhaseChip } from '../PhaseChip';
import type { LifecycleRailProps } from './types';

/**
 * The whole lifecycle in one strip: five stage bars, every phase as a chip
 * beneath its stage, and the phase you are in filled rather than outlined.
 *
 * **On the phase, process and method pages and not on the L1 grid**, which
 * already *is* the lifecycle. Below L1 it is the only thing saying where you are.
 *
 * **Groups and orders its own input**, because `GET /content/phases` sorts by
 * `number` across the whole lifecycle rather than within a stage. Stages go in
 * `stageNumber` order, which puts cross-cutting Delivery first.
 */

export const LifecycleRail = ({ phases, activePhaseSlug }: LifecycleRailProps) => {
  // The page's own loading and error states cover the reason; a rail of empty
  // bars would be a second, worse version of that.
  if (phases.length === 0) {
    return null;
  }

  const stages = groupPhasesByStage(phases);

  return (
    /*
     * **Scrolls sideways rather than reflowing**, with no mobile design to
     * follow. Eleven chips do not fit a phone; squeezing crushes Delivery and
     * Discovery until their labels overflow their bars, and hiding the rail takes
     * away the only thing saying where you are below L1.
     *
     * `min-width: max-content` is what makes that work both ways — the columns
     * still grow on a wide viewport and stop shrinking at their content.
     */
    <Box component="nav" aria-label="Lifecycle" sx={{ overflowX: 'auto', width: '100%' }}>
      <Box sx={{ display: 'flex', gap: 2, width: '100%', minWidth: 'max-content' }}>
        {stages.map(({ stage, phases: stagePhases }) => {
          const colour = stageColourFor(stage, 'fill');

          return (
            <Box
              key={stage}
              sx={{
                // Grows in proportion to how many phases it holds, from a
                // content-sized base — so Delivery's single chip stays legible.
                flexGrow: stagePhases.length,
                flexShrink: 0,
                flexBasis: 'auto',
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
              }}
            >
              <Box
                sx={{
                  backgroundColor: colour,
                  borderRadius: `${String(tokens.radius.md)}px`,
                  paddingBlock: 1,
                  paddingInline: 3,
                  textAlign: 'center',
                }}
              >
                <Typography
                  variant="caption"
                  component="span"
                  sx={{
                    fontFamily: tokens.fontFamily.heading,
                    fontWeight: 600,
                    // The bar is a solid ground under white, which is what
                    // `fill` is for and why it is the same in both modes.
                    color: 'text.onBrand',
                    letterSpacing: '0.04em',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {stageLabelFor(stage)}
                </Typography>
              </Box>

              {/* The chips share their stage's width rather than centring
                  under it, so each stage reads as one band: three chips under
                  DESIGN span exactly the DESIGN bar above them. */}
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'stretch' }}>
                {stagePhases.map((phase) => (
                  <PhaseChip
                    key={phase.id}
                    label={phase.shortTitle ?? phase.name}
                    stage={phase.stage}
                    active={phase.slug === activePhaseSlug}
                    to={`${SDLC_PATH}/${phase.path}`}
                  />
                ))}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};
